import * as React from 'react';
import { useLazyQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import { AsyncLoadPagedResult, AsyncLoadPagedOptions } from '../../../../shared/abstractStore';
import { useFetchMoreCallback } from '../../../../shared/useFetchMoreCallback';
import { listProfilesViewModelQuery, listProfilesViewModelQueryVariables } from '../../generated/listProfilesViewModelQuery';
import { mainApiConfig } from '../../../../configure/mainApiConfig';
import { profileFieldsFragment } from '../../models/Profile';
import { subscriptionDepartmentFieldsFragment } from '../../models/SubscriptionDepartment';

export interface listProfilesViewModelAsyncLoadPageOptions extends AsyncLoadPagedOptions {
    subscriptionId?: string | null | undefined,
    subscriptionDepartmentId?: string | null | undefined,
    notSubscriptionDepartmentId?: string | null | undefined,
}

/**
 * View model for ListProfiles that replaces the call to useProfiles() and loads the right related data.
 * @param options
 */
export function useListProfilesViewModel(options: listProfilesViewModelAsyncLoadPageOptions = {}): AsyncLoadPagedResult<listProfilesViewModelQuery> {
    // Query the data we need form the api.
    const [load, { data, loading, error, refetch, fetchMore: fetchMoreFromStore }] = useLazyQuery<listProfilesViewModelQuery, listProfilesViewModelQueryVariables>(
        gql`
        query listProfilesViewModelQuery ($offset: Int, $first: Int, $subscriptionId: ID, $subscriptionDepartmentId: ID, $notSubscriptionDepartmentId: ID) {
            items: profiles (offset: $offset, first: $first, subscriptionId: $subscriptionId, subscriptionDepartmentId: $subscriptionDepartmentId, notSubscriptionDepartmentId: $notSubscriptionDepartmentId) {
                ...profileFields
                user {
                  id
                  email
                  roleGroup {
                    id
                    name
                  }
                }
            }
            departments: subscriptionDepartments(subscriptionId: $subscriptionDepartmentId) {
                ...subscriptionDepartmentFields
            }
        }
        ${profileFieldsFragment}
        ${subscriptionDepartmentFieldsFragment}
        `,
        {
            variables: {
                offset: options.startOffset,
                first: options.pageSize,
                subscriptionId: options.subscriptionId,
                subscriptionDepartmentId: options.subscriptionDepartmentId,
                notSubscriptionDepartmentId: options.notSubscriptionDepartmentId,
            },
            fetchPolicy: options.fetchPolicy ?? mainApiConfig.defaultFetchPolicy,
            pollInterval: options.pollInterval,
            onCompleted: (data) => {
                if (options.onCompleted) {
                    options.onCompleted(data);
                }
            }
        }
    );

    // Run the query (unless we are being lazy).
    React.useEffect(() => {
        if (!options.lazy) {
            load();
        }
    }, [options.lazy, load])

    // Get the functions used to manage paging.
    const { fetchMore, hasMore: _hasMore } = useFetchMoreCallback<listProfilesViewModelQuery>(
        fetchMoreFromStore,
        (options?.startOffset ?? 0) + (data?.items?.length ?? 0),
        (prevResults, newResults) => ({
            ...prevResults,
            ...newResults,

            items: [...(prevResults?.items ?? []), ...(newResults?.items ?? [])],
            departments: newResults?.departments ?? []
        }),
        (newResults) => newResults?.items?.length
    );
    const hasMore = React.useCallback(() => !options.pageSize ? false : _hasMore(), [options.pageSize, _hasMore]);

    // Return the results.
    const refresh = React.useCallback(async () => { !refetch ? await load() : await refetch(); }, [refetch, load]);
    return React.useMemo(() => ({
        data: data ?? ({} as any),
        refresh: refresh,
        isLoading: loading,
        errors: error,
        fetchMore: fetchMore,
        hasMore: hasMore,
    }), [data, refresh, loading, error, fetchMore, hasMore]);
}